import { EmptyView } from "@/components";
import { DatasetCard } from "@/features";
import { DiscoverResultsLoading } from "@/features/discover/results";
import { useIntersectionObserver } from "@/hooks";
import type { Dataset, DatasetResponse } from "@/types/europafacts";
import { Flex } from "@mantine/core";
import { useCallback } from "react";
import { useOutletContext } from "react-router";
import "./DiscoverResultsPage.css";

export function DiscoverResultsPage() {
  const { datasets, loading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useOutletContext<{
      result?: DatasetResponse;
      datasets: Dataset[];
      loading: boolean;
      requestDuration: number;
      fetchNextPage?: () => void;
      hasNextPage?: boolean;
      isFetchingNextPage?: boolean;
    }>();

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (
        fetchNextPage &&
        entries[0]?.isIntersecting &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const observerTarget = useIntersectionObserver(handleIntersection, {
    threshold: 0.1,
    rootMargin: "400px",
  });

  if (loading) {
    return <DiscoverResultsLoading count={10} />;
  }

  function ResultsPagination() {
    if (!hasNextPage) return null;
    return (
      <div ref={observerTarget} style={{ minHeight: "300px" }}>
        {isFetchingNextPage && <DiscoverResultsLoading count={4} />}
      </div>
    );
  }

  if (datasets.length === 0) {
    return (
      <EmptyView
        height="60vh"
        title="No datasets found"
        description="Please try a different search query"
      />
    );
  }

  return (
    <Flex direction="column" py={20} gap="md">
      {datasets.map((dataset: Dataset) => (
        <DatasetCard key={dataset.db_table_id} dataset={dataset} />
      ))}
      <ResultsPagination />
    </Flex>
  );
}
